import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Rocket, Users, Clock, ArrowRight, Envelope } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { sendEmailJS } from '../utils/emailjs';

const StartProjectPage = () => {
  const { t } = useTranslation();
  const form = useRef();
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState(null);

  const services = ['websites', 'webshops', 'seo', 'optimization', 'partner'];

  const sendEmail = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setError(null);
    setIsSent(false);

    try {
      await sendEmailJS(form.current, process.env.REACT_APP_EMAILJS_START_PROJECT_TEMPLATE_ID, {
        onSuccess: () => {
          setIsSent(true);
          form.current.reset();
          
          if(typeof window.gtag_report_conversion === 'function') {
            window.gtag_report_conversion();
          }
        },
        onError: (error) => {
          setError(t('startProjectPage.form.errorMessage'));
        }
      });
    } catch (error) {
      // Error is already handled in onError callback
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        
        <div className="relative text-center py-24 px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-[#0D2B45]/10 text-[#0D2B45] px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <Rocket size={16} className="ph-thin" />
              <span>Laten we samen bouwen</span>
            </motion.div>
            
            <h1 className="text-5xl md:text-7xl font-bold font-heading mb-6 text-[#0D2B45]">
              {t('startProjectPage.hero.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('startProjectPage.hero.subtitle')}
            </p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-wrap justify-center gap-6 mt-8"
            >
              <div className="flex items-center gap-2 text-gray-600">
                <Users size={20} className="ph-thin text-[#0D2B45]" />
                <span className="text-sm">{t('startProjectPage.features.personalApproach')}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Clock size={20} className="ph-thin text-[#0D2B45]" />
                <span className="text-sm">{t('startProjectPage.features.fastResponse')}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <CheckCircle size={20} className="ph-thin text-[#0D2B45]" />
                <span className="text-sm">{t('startProjectPage.features.guaranteedQuality')}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
          
          {/* Project Form */}
          <motion.div
            className="lg:col-span-1 bg-white p-8 sm:p-10 rounded-2xl shadow-xl border border-gray-100 relative overflow-hidden"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#0D2B45] rounded-lg flex items-center justify-center">
                  <Rocket size={20} className="ph-thin text-white" />
                </div>
                <h2 className="text-3xl font-bold font-heading">{t('startProjectPage.form.title')}</h2>
              </div>
            <form ref={form} onSubmit={sendEmail} className="space-y-6">
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="group">
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-[#0D2B45] transition-colors">{t('startProjectPage.form.fullName')}</label>
                  <input type="text" id="name" name="name" required className="mt-1 block w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0D2B45]/20 focus:border-[#0D2B45] transition-all duration-200 hover:bg-gray-50" />
                </div>
                <div className="group">
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-[#0D2B45] transition-colors">{t('startProjectPage.form.company')}</label>
                  <input type="text" id="company" name="company" className="mt-1 block w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0D2B45]/20 focus:border-[#0D2B45] transition-all duration-200 hover:bg-gray-50" />
                </div>
              </div>
              <div className="group">
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-[#0D2B45] transition-colors">{t('startProjectPage.form.emailAddress')}</label>
                <input type="email" id="email" name="from_email" required className="mt-1 block w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0D2B45]/20 focus:border-[#0D2B45] transition-all duration-200 hover:bg-gray-50" />
              </div>
               <div className="group">
                <label className="block text-sm font-semibold text-gray-700 mb-3">{t('startProjectPage.form.servicesOfInterest')}</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services.map(serviceKey => {
                    const serviceName = t(`startProjectPage.form.services.${serviceKey}`);
                    return (
                      <div key={serviceKey} className="flex items-center p-3 bg-gray-50/50 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                        <input id={serviceName} name="services" type="checkbox" value={serviceName} className="h-4 w-4 text-[#0D2B45] focus:ring-[#0D2B45] border-gray-300 rounded" />
                        <label htmlFor={serviceName} className="ml-3 block text-sm font-medium text-gray-800 cursor-pointer">{serviceName}</label>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="group">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-[#0D2B45] transition-colors">{t('startProjectPage.form.projectDescription')}</label>
                <textarea id="message" name="message" placeholder={t('startProjectPage.form.projectPlaceholder')} rows="6" required className="mt-1 block w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0D2B45]/20 focus:border-[#0D2B45] transition-all duration-200 hover:bg-gray-50 resize-none"></textarea>
              </div>
              <div>
                <button type="submit" disabled={isSending || isSent} className="w-full inline-flex justify-center items-center gap-3 bg-[#0D2B45] text-white px-8 py-4 rounded-xl hover:bg-[#0D2B45]/90 transition-all duration-300 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none">
                  {isSending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t('startProjectPage.form.submittingButton')}
                    </>
                  ) : isSent ? (
                    <>
                      <CheckCircle size={20} className="ph-thin" />
                      {t('startProjectPage.form.sentButton')}
                    </>
                  ) : (
                    <>
                      <ArrowRight size={20} className="ph-thin" />
                      {t('startProjectPage.form.submitButton')}
                    </>
                  )}
                </button>
              </div>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm"
                >
                  {error}
                </motion.div>
              )}
              {isSent && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
                >
                  <CheckCircle size={16} className="ph-thin text-green-600" />
                  {t('startProjectPage.form.successMessage')}
                </motion.div>
              )}
            </form>
            </div>
          </motion.div>

           {/* What Happens Next */}
          <motion.div 
            className="lg:col-span-1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="p-8 bg-white rounded-2xl shadow-xl border border-gray-100 sticky top-24 overflow-hidden">
                
                <div className="relative">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 bg-[#0D2B45] rounded-lg flex items-center justify-center">
                      <Clock size={20} className="ph-thin text-white" />
                    </div>
                    <h3 className="text-3xl font-bold font-heading">{t('startProjectPage.whatHappensNext.title')}</h3>
                  </div>
                  
                  <div className="space-y-6">
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.6 }}
                      className="flex items-start p-4 bg-blue-50 rounded-xl border border-blue-100"
                    >
                      <div className="w-8 h-8 bg-[#0D2B45] rounded-full flex items-center justify-center flex-shrink-0 mt-1 mr-4">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-2">{t('startProjectPage.whatHappensNext.step1.title')}</h4>
                        <p className="text-gray-600 leading-relaxed">{t('startProjectPage.whatHappensNext.step1.description')}</p>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.7 }}
                      className="flex items-start p-4 bg-blue-50 rounded-xl border border-blue-100"
                    >
                      <div className="w-8 h-8 bg-[#0D2B45] rounded-full flex items-center justify-center flex-shrink-0 mt-1 mr-4">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-2">{t('startProjectPage.whatHappensNext.step2.title')}</h4>
                        <p className="text-gray-600 leading-relaxed">{t('startProjectPage.whatHappensNext.step2.description')}</p>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.8 }}
                      className="flex items-start p-4 bg-blue-50 rounded-xl border border-blue-100"
                    >
                      <div className="w-8 h-8 bg-[#0D2B45] rounded-full flex items-center justify-center flex-shrink-0 mt-1 mr-4">
                        <span className="text-white font-bold text-sm">3</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-2">{t('startProjectPage.whatHappensNext.step3.title')}</h4>
                        <p className="text-gray-600 leading-relaxed">{t('startProjectPage.whatHappensNext.step3.description')}</p>
                      </div>
                    </motion.div>
                  </div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                    className="mt-8 pt-6 border-t border-gray-200 bg-blue-50 p-6 rounded-xl"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <Users size={20} className="ph-thin text-[#0D2B45]" />
                      <h4 className="font-bold text-lg">{t('startProjectPage.whatHappensNext.generalQuestion.title')}</h4>
                    </div>
                    <p className="text-gray-600 leading-relaxed mb-4">{t('startProjectPage.whatHappensNext.generalQuestion.description')}</p>
                    
                    <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-[#0D2B45] transition-colors">
                      <div className="w-10 h-10 bg-[#0D2B45] rounded-lg flex items-center justify-center">
                        <Envelope size={18} className="ph-thin text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Direct contact</p>
                        <a 
                          href="mailto:info@juliux.nl" 
                          className="text-[#0D2B45] font-semibold hover:text-[#0D2B45]/80 transition-colors"
                        >
                          info@juliux.nl
                        </a>
                      </div>
                    </div>
                  </motion.div>
                </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default StartProjectPage; 